import {
  SPONSOR_ORDERS,
  TOURNAMENT_ORDERS,
  REGISTRATION_GROSS,
  REGISTRATION_GROSS_TOTAL,
  REGISTRATION_FEE_TOTAL,
  REGISTRATION_CONCEPT,
  REGION_BY_ID,
  packageById,
  promotionById,
  formatUSD,
  ctr,
  PLATFORM_FEE_RATE,
} from './revenueData';
import type { CampaignStatus } from './revenueData';

function StatusChip({ status }: { status: CampaignStatus }) {
  const tone: Record<CampaignStatus, string> = {
    Live: 'bg-primary-500 text-background-50',
    Scheduled: 'bg-secondary-100 text-secondary-900 border border-secondary-200',
    'In review': 'bg-accent-100 text-accent-900 border border-accent-300',
    'Renewal due': 'bg-foreground-950 text-accent-400',
  };
  return <span className={`font-label font-bold uppercase tracking-wide text-[10px] px-2.5 py-1 rounded-sm whitespace-nowrap ${tone[status]}`}>{status}</span>;
}

export default function RevenueLedger() {
  const renewals = SPONSOR_ORDERS.filter((o) => o.renewsInDays > 0 && o.renewsInDays <= 30).sort((a, b) => a.renewsInDays - b.renewsInDays);
  const topPerformers = [...SPONSOR_ORDERS].filter((o) => o.impressions > 0).sort((a, b) => b.leads - a.leads).slice(0, 5);
  const maxLeads = Math.max(...topPerformers.map((o) => o.leads), 1);

  return (
    <div className="space-y-6">
      {/* Active campaigns */}
      <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-background-200 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading font-bold text-xl text-foreground-950">Active sponsorship campaigns</h3>
          <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500">{SPONSOR_ORDERS.length} sample campaigns</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left font-label uppercase tracking-widest text-[10px] text-foreground-500 border-b border-background-200">
                <th className="px-5 py-3 font-semibold">Sponsor</th>
                <th className="px-3 py-3 font-semibold">Package</th>
                <th className="px-3 py-3 font-semibold">Region</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 font-semibold text-right">Monthly</th>
                <th className="px-3 py-3 font-semibold text-right">CTR</th>
                <th className="px-3 py-3 font-semibold text-right">Leads</th>
                <th className="px-5 py-3 font-semibold text-right">Renews</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {SPONSOR_ORDERS.map((o) => (
                <tr key={o.id}>
                  <td className="px-5 py-3 text-foreground-950 font-medium">{o.sponsor}</td>
                  <td className="px-3 py-3 text-foreground-600">{packageById(o.packageId)?.name}</td>
                  <td className="px-3 py-3 text-foreground-600 whitespace-nowrap">{REGION_BY_ID[o.regionId].city}</td>
                  <td className="px-3 py-3"><StatusChip status={o.status} /></td>
                  <td className="px-3 py-3 text-right text-foreground-950 font-semibold">{formatUSD(o.monthly)}</td>
                  <td className="px-3 py-3 text-right text-foreground-600">{ctr(o.impressions, o.clicks)}</td>
                  <td className="px-3 py-3 text-right text-foreground-600">{o.leads || '—'}</td>
                  <td className="px-5 py-3 text-right text-foreground-600 whitespace-nowrap">{o.renewsInDays > 0 ? `${o.renewsInDays}d` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tournament promotion orders */}
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-background-200">
            <h3 className="font-heading font-bold text-xl text-foreground-950">Tournament promotion orders</h3>
          </div>
          <ul className="divide-y divide-background-200 max-h-[320px] overflow-y-auto">
            {TOURNAMENT_ORDERS.map((o) => (
              <li key={o.id} className="px-5 py-3 flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-950 truncate">{o.tournament}</p>
                  <p className="text-xs text-foreground-500 truncate">{promotionById(o.promotionId)?.name} · {REGION_BY_ID[o.regionId].city}</p>
                </div>
                <span className="font-heading font-extrabold text-lg text-foreground-950">{formatUSD(o.amount)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Registration revenue */}
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h3 className="font-heading font-bold text-xl text-foreground-950">Registration revenue examples</h3>
          <p className="mt-2 text-xs text-foreground-500">With the managed service, the platform keeps an illustrative {Math.round(PLATFORM_FEE_RATE * 100)}% fee on registrations it processes.</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-md bg-background-100 border border-background-200 px-4 py-3">
              <p className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Gross handled</p>
              <p className="mt-1 font-heading font-extrabold text-2xl text-foreground-950">{formatUSD(REGISTRATION_GROSS_TOTAL)}</p>
            </div>
            <div className="rounded-md bg-background-100 border border-background-200 px-4 py-3">
              <p className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Platform fees</p>
              <p className="mt-1 font-heading font-extrabold text-2xl text-accent-700">{formatUSD(REGISTRATION_FEE_TOTAL)}</p>
            </div>
            <div className="rounded-md bg-background-100 border border-background-200 px-4 py-3">
              <p className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Events</p>
              <p className="mt-1 font-heading font-extrabold text-2xl text-foreground-950">{REGISTRATION_GROSS.length}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 text-xs text-foreground-700 bg-secondary-50 border border-secondary-200 rounded-md px-3 py-2">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line"></i></span>{REGISTRATION_CONCEPT.individual.label}: {formatUSD(REGISTRATION_CONCEPT.individual.example)}
            </span>
            <span className="inline-flex items-center gap-2 text-xs text-foreground-700 bg-secondary-50 border border-secondary-200 rounded-md px-3 py-2">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-group-line"></i></span>{REGISTRATION_CONCEPT.foursome.label}: {formatUSD(REGISTRATION_CONCEPT.foursome.example)}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming renewals */}
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h3 className="font-heading font-bold text-xl text-foreground-950">Upcoming renewals</h3>
          <p className="mt-1 text-xs text-foreground-500">Campaigns renewing within 30 days.</p>
          <ul className="mt-4 space-y-3">
            {renewals.map((o) => (
              <li key={o.id} className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center font-heading font-extrabold text-sm shrink-0">{o.renewsInDays}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-950 truncate">{o.sponsor}</p>
                  <p className="text-xs text-foreground-500 truncate">{packageById(o.packageId)?.name} · {REGION_BY_ID[o.regionId].city}</p>
                </div>
                <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500 whitespace-nowrap">renews in {o.renewsInDays}d</span>
              </li>
            ))}
            {renewals.length === 0 && <li className="text-sm text-foreground-500">No renewals due in the next 30 days.</li>}
          </ul>
        </div>

        {/* Sponsor performance */}
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h3 className="font-heading font-bold text-xl text-foreground-950">Sponsor performance summaries</h3>
          <p className="mt-1 text-xs text-foreground-500">Top campaigns by leads generated (sample).</p>
          <ul className="mt-4 space-y-4">
            {topPerformers.map((o) => (
              <li key={o.id}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-800 truncate">{o.sponsor}</span>
                  <span className="font-label font-semibold text-foreground-600 whitespace-nowrap">{o.leads} leads · {ctr(o.impressions, o.clicks)} CTR</span>
                </div>
                <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
                  <div className="h-full bg-primary-500 rounded-full" style={{ width: `${(o.leads / maxLeads) * 100}%` }}></div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}